import {inject, Injectable} from '@angular/core';
import {SearchService} from '../../../core/search.service';
import {SearchParameters} from '../../../core/definitions/search-parameters';
import {LoggerService} from '../../../core/logger.service';
import {ConceptOption} from '../concept-picker/concept-option.model';
import {NAMED_TYPE_CONCEPT_TYPES, NamedObjectType} from './group-card.model';

// How long a fetched type list serves every card before it is fetched
// again. Museum staff edit these lists rarely, and a term they add shows
// up after this long without a page reload.
const OPTIONS_TTL_MS = 5 * 60 * 1000;
// More than either list holds; both are short, flat lists.
const OPTIONS_MAX_ROWS = 1000;

/**
 * The options of a Named sub-block's type picker: every valid term of
 * the object type's list (`agent_type_l` or `named_object_type_l`),
 * unfiltered, by name, each `{id, name, has_authority}` with `id` the
 * term's `artifact_id` - the shape `/batch_fields` gives a concept
 * picker. Both lists are flat and carry no `ref_filter`, so the terms are
 * read from Solr directly.
 *
 * A list is fetched once for every card on the page, and kept for
 * `OPTIONS_TTL_MS`. A failed fetch is logged and rejects, and is not
 * kept: the next caller fetches again. The search itself already shows
 * the operator its error.
 */
@Injectable({providedIn: 'root'})
export class NamedTypeOptionsService {
  private readonly searchService: SearchService = inject(SearchService);
  private readonly logger: LoggerService = inject(LoggerService);

  private readonly cache = new Map<NamedObjectType, {fetchedAt: number; options: Promise<ConceptOption[]>}>();

  options(objectType: NamedObjectType): Promise<ConceptOption[]> {
    const cached = this.cache.get(objectType);
    if (cached && Date.now() - cached.fetchedAt < OPTIONS_TTL_MS) {
      return cached.options;
    }
    const options = this.fetch(objectType);
    this.cache.set(objectType, {fetchedAt: Date.now(), options});
    return options;
  }

  private async fetch(objectType: NamedObjectType): Promise<ConceptOption[]> {
    const conceptType = NAMED_TYPE_CONCEPT_TYPES[objectType];
    try {
      const res = await this.searchService.search({
        fq: [`object_type:${conceptType}`, 'valid:true'],
        fl: ['artifact_id', 'artifact_name', 'has_authority'],
        rows: OPTIONS_MAX_ROWS,
        sort: 'artifact_name_upper asc',
      } as SearchParameters);
      return (res.artifacts ?? [])
        .filter(art => !!art.artifact_id)
        .map(art => ({id: art.artifact_id, name: art.artifact_name, has_authority: !!art.has_authority}));
    } catch (e) {
      this.logger.error(`named type options: fetching ${conceptType} failed: ${e}`);
      this.cache.delete(objectType);
      throw e;
    }
  }
}
